import httpService from "../../utils/httpService"
import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {AppThunk} from "../../rootReducer"
import {getAccessToken} from "../../utils/common"
import dayjs from "dayjs"
import {setToastr} from "../App/appSlice"

export type Defect = {
    annualTestDefectId: number
    failureItemNo: number
    failureReason: string
    severityCode: string
    severityDescription: string
}
export interface VehicleDetail {
    vehicleId: number
    registration: string
    make: string
    model: string
    vehicleType: string
    vehicleClass: string
    manufactureDate: Date | null
    registrationDate: Date | null
    annualTestExpiryDate: Date | null
    dvsaFetchDate: Date | null
    newVehicle: false
}
export const vehicleInit = () => {
    return {
        vehicleId: 0,
        registration: "",
        make: "",
        model: "",
        vehicleType: "",
        vehicleClass: "",
        manufactureDate: null,
        registrationDate: null,
        annualTestExpiryDate: null,
        dvsaFetchDate: null,
        newVehicle: false,
    } as VehicleDetail
}

export type AnnualTest = {
    annualTestId: number
    testDate: string
    testType: string
    testResult: string
    testCertificateNumber: string
    expiryDate: Date | null
    numberOfDefectsAtTest: number
    numberOfAdvisoryDefectsAtTest: number
    location: string
    defects: Defect[]
    expanded: boolean
}

export type Vehicle = {
    vehicleId: number
    registration: string
    vin: string
    make: string
    model: string
    vehicleType: string
    vehicleClass: string
    manufactureDate: any
    registrationDate: any
    annualTestExpiryDate: any
    active: boolean
    expanded: boolean
    annualTests: AnnualTest[]
    dvsaFetchDate: any
    editable: false
}

export const vehicleInitial: Vehicle = {
    vehicleId: 0,
    registration: "",
    vin: "",
    make: "",
    model: "",
    vehicleType: "",
    vehicleClass: "",
    manufactureDate: "",
    registrationDate: "",
    annualTestExpiryDate: "",
    active: false,
    expanded: false,
    annualTests: [],
    dvsaFetchDate: "",
    editable: false,
}

type VehicleState = {
    vehicleID: number
    vehicleSearchText: string
    isVehicleLoading: boolean
    vehicleData: Vehicle[]
    vehicleError: string
    RegOrVinSearchText: string
    RegOrVinLoading: boolean
    RegOrVinData: VehicleDetail
    RegOrVinError: string
    loadingMakes: boolean
    makeList: string[]
    loadingModels: boolean
    modelsList: string[]
    loadingVehicleClass: boolean
    vehicleClass: string
    loadingVehicleType: boolean
    vehicleTypes: string
    showDialog: boolean
    savingVehicle: boolean
    showsuccessToastr: boolean
    showErrorToastr: boolean
    errorToastr: {isActive: boolean; error: string}
    isRefreshDVSAButtonVisible: boolean
}

const initialState: VehicleState = {
    vehicleID: 0,
    isVehicleLoading: false,
    vehicleData: [],
    vehicleError: "",
    vehicleSearchText: "",
    RegOrVinSearchText: "",
    RegOrVinLoading: false,
    RegOrVinData: vehicleInit(),
    RegOrVinError: "",
    loadingMakes: false,
    makeList: [],
    loadingModels: false,
    modelsList: [],
    loadingVehicleClass: false,
    vehicleClass: "",
    loadingVehicleType: false,
    vehicleTypes: "",
    showDialog: false,
    savingVehicle: false,
    showsuccessToastr: false,
    showErrorToastr: false,
    errorToastr: {isActive: false, error: ""},
    isRefreshDVSAButtonVisible: false,
}

const VehicleSlice = createSlice({
    name: "Vehicle",
    initialState,
    reducers: {
        setVehicleSearchText(state, action: PayloadAction<string>) {
            state.vehicleSearchText = action.payload
        },
        setVehicleLoading(state, action: PayloadAction<boolean>) {
            state.isVehicleLoading = action.payload
        },
        setVehicleData(state, action: PayloadAction<any>) {
            const vehicles = action.payload
            const transformedVehicles = vehicles.map((vehicle: any) => ({
                ...vehicle,
                expanded: false,
                annualTests: vehicle.annualTests.map((annualTest: any) => ({...annualTest, expanded: false})),
            }))
            state.vehicleData = transformedVehicles
        },
        setVehicleError(state, action: PayloadAction<string>) {
            state.vehicleError = action.payload
        },
        RegOrVinSearchText(state, action: PayloadAction<string>) {
            state.RegOrVinSearchText = action.payload
        },
        RegOrVinLoading(state, action: PayloadAction<boolean>) {
            state.RegOrVinLoading = action.payload
        },
        RegOrVinData(state, action: PayloadAction<VehicleDetail>) {
            state.RegOrVinData = action.payload
        },
        RegOrVinError(state, action: PayloadAction<string>) {
            state.RegOrVinError = action.payload
        },
        setMakeData(state, action: PayloadAction<[boolean, string[]]>) {
            state.loadingMakes = action.payload[0]
            state.makeList = action.payload[1]
        },
        setMakeModelsData(state, action: PayloadAction<[boolean, string[]]>) {
            state.loadingModels = action.payload[0]
            state.modelsList = action.payload[1]
        },
        setVehicleClassData(state, action: PayloadAction<[boolean, string]>) {
            state.loadingVehicleClass = action.payload[0]
            state.vehicleClass = action.payload[1]
        },
        setVehicleTypes(state, action: PayloadAction<[boolean, string]>) {
            state.loadingVehicleType = action.payload[0]
            state.vehicleTypes = action.payload[1]
        },
        toggleModalPopup(state, action: PayloadAction<boolean>) {
            state.showDialog = action.payload
            state.errorToastr = {isActive: false, error: ""}
        },
        savingVehicleData(state, action: PayloadAction<boolean>) {
            state.savingVehicle = action.payload
        },
        setshowSuccessToastr(state, action: PayloadAction<boolean>) {
            state.showsuccessToastr = action.payload
        },
        setshowErrorToastr(state, action: PayloadAction<{isActive: boolean; error: string}>) {
            state.errorToastr = action.payload
        },
        setRefreshDVSAVisibleButton(state, action: PayloadAction<boolean>) {
            state.isRefreshDVSAButtonVisible = action.payload
        },
    },
})

export const {
    setVehicleSearchText,
    setVehicleLoading,
    setVehicleData,
    setVehicleError,
    RegOrVinSearchText,
    RegOrVinLoading,
    RegOrVinData,
    RegOrVinError,
    setMakeData,
    setMakeModelsData,
    setVehicleClassData,
    setVehicleTypes,
    toggleModalPopup,
    savingVehicleData,
    setshowSuccessToastr,
    setshowErrorToastr,
    setRefreshDVSAVisibleButton,
} = VehicleSlice.actions

export default VehicleSlice.reducer

export const vehicleSearchText =
    (filterText: string): AppThunk =>
    dispatch =>
        dispatch(setVehicleSearchText(filterText))

const formatAsDate = (dateColumn: Date | null) => (dateColumn !== null ? new Date(dateColumn) : null)

export const getVehicles =
    (filterText: string, checkDvsa: boolean): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setVehicleLoading(true))
        httpService(getAccessToken(getState()))
            .get(`vehicles/longsearch?searchtext=${encodeURIComponent(filterText)}&checkDvsa=${checkDvsa}`)
            .then(res => {
                const vehicles = res.data.map((data: Vehicle) => ({
                    ...data,
                    manufactureDate: formatAsDate(data.manufactureDate),
                    registrationDate: formatAsDate(data.registrationDate),
                    annualTestExpiryDate: formatAsDate(data.annualTestExpiryDate),
                }))
                dispatch(setVehicleLoading(false))
                dispatch(setVehicleData(vehicles))
                dispatch(setRefreshDVSAVisibleButton(false))
            })
            .catch(error => {
                dispatch(setVehicleLoading(false))
                dispatch(setVehicleError(error))
                dispatch(setRefreshDVSAVisibleButton(true))
                if (checkDvsa) {
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: "Vehicle unavailable in DVSA",
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    )
                    dispatch(setRefreshDVSAVisibleButton(false))
                }
            })
    }

export const RegOrVinSearch =
    (filterText: string): AppThunk =>
    dispatch =>
        dispatch(RegOrVinSearchText(filterText))

export const resetVehicleSection =
    (vehicle: VehicleDetail): AppThunk =>
    (dispatch: any) => {
        const resetVehicleInfo = {
            ...vehicle,
            vehicleId: 0,
            registration: "",
            make: "",
            model: "",
            vehicleType: "",
            vehicleClass: "",
            manufactureDate: null,
            annualTestExpiryDate: null,
            registrationDate: null,
            dvsaFetchDate: null,
            newVehicle: false,
        } as VehicleDetail
        dispatch(RegOrVinData(resetVehicleInfo))
        dispatch(setshowErrorToastr({isActive: false, error: ""}))
    }

export const getRegOrVin =
    (vehicleRegNo: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(RegOrVinLoading(true))
        httpService(getAccessToken(getState()))
            .get(`vehicles/search/${vehicleRegNo}`)
            .then(res => {
                const v = res.data as Vehicle
                const vehicleData = {
                    vehicleId: v.vehicleId,
                    registration: v.registration ?? vehicleRegNo,
                    make: v.make ?? "",
                    model: v.model ?? "",
                    vehicleType: v.vehicleType ?? "",
                    vehicleClass: v.vehicleClass ?? "",
                    manufactureDate: formatAsDate(v.manufactureDate),
                    annualTestExpiryDate: formatAsDate(v.annualTestExpiryDate),
                    registrationDate: formatAsDate(v.registrationDate),
                    dvsaFetchDate: v.dvsaFetchDate,
                    newVehicle: v.vehicleId === 0,
                } as VehicleDetail
                vehicleData.vehicleId === 0 &&
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: "Vehicle unavailable in DVSA",
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    )
                dispatch(RegOrVinData(vehicleData))
                dispatch(RegOrVinLoading(false))
            })
            .catch(error => {
                dispatch(RegOrVinLoading(false))
            })
    }

export const fetchMakeData = (): AppThunk => (dispatch: any, getState) => {
    dispatch(setMakeData([true, []]))
    httpService(getAccessToken(getState()))
        .get(`vehicles/make`)
        .then(res => {
            const makeList = res.data as string[]
            dispatch(setMakeData([false, makeList]))
        })
        .catch(error => {
            dispatch(setMakeData([false, []]))
        })
}

export const fetchModelData =
    (make: string): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setMakeModelsData([true, []]))
        httpService(getAccessToken(getState()))
            .get(`vehicles/model?make=${make}`)
            .then(res => {
                const modelList = res.data as string[]
                dispatch(setMakeModelsData([false, modelList]))
            })
            .catch(error => {
                dispatch(setMakeModelsData([false, []]))
            })
    }

export const fetchVehicleType = (): AppThunk => (dispatch: any, getState) => {
    dispatch(setVehicleTypes([true, ""]))
    httpService(getAccessToken(getState()))
        .get(`vehicles/vehicleType`)
        .then(res => {
            const vehicleType = res.data as string
            dispatch(setVehicleTypes([false, vehicleType]))
        })
        .catch(error => {
            dispatch(setVehicleTypes([false, ""]))
        })
}

export const fetchVehicleClassData = (): AppThunk => (dispatch: any, getState) => {
    dispatch(setVehicleClassData([true, ""]))
    httpService(getAccessToken(getState()))
        .get(`vehicles/vehicleclass`)
        .then(res => {
            const classList = res.data as string
            dispatch(setVehicleClassData([false, classList]))
        })
        .catch(error => {
            dispatch(setVehicleClassData([false, ""]))
        })
}
export const saveVehicleDetails =
    (data: VehicleDetail): AppThunk =>
    (dispatch: any, getState) => {
        const vehicle = {
            ...data,
            manufactureDate: data.manufactureDate != null ? dayjs(data.manufactureDate).format("YYYY-MM-DD") : null,
            annualTestExpiryDate:
                data.annualTestExpiryDate != null ? dayjs(data.annualTestExpiryDate).format("YYYY-MM-DD") : null,
            registrationDate: data.registrationDate != null ? dayjs(data.registrationDate).format("YYYY-MM-DD") : null,
        }

        dispatch(savingVehicleData(true))

        if (vehicle.vehicleId === 0) {
            httpService(getAccessToken(getState()))
                .post(`vehicles`, vehicle)
                .then((res: any) => {
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: "Vehicle saved",
                            delay: 3000,
                            title: "Success",
                            type: "light",
                            description: "",
                        }),
                    )
                    dispatch(savingVehicleData(false))
                    dispatch(toggleModalPopup(false))
                })
                .catch((res: any) => {
                    dispatch(savingVehicleData(false))
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: "Vehicle not saved",
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    )
                })
        } else {
            httpService(getAccessToken(getState()))
                .put(`vehicles/${vehicle.vehicleId}`, vehicle)
                .then((res: any) => {
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: "Vehicle saved",
                            delay: 3000,
                            title: "Success",
                            type: "light",
                            description: "",
                        }),
                    )
                    dispatch(savingVehicleData(false))
                    dispatch(toggleModalPopup(false))
                })
                .catch((res: any) => {
                    dispatch(savingVehicleData(false))
                    dispatch(
                        setToastr({
                            isActive: true,
                            message: "Vehicle not saved",
                            delay: 3000,
                            title: "Error",
                            type: "danger",
                            description: "",
                        }),
                    )
                })
        }
    }
